import { motion } from "framer-motion";
import { Trophy, Calendar, BarChart3, MapPin, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { tournaments, schedule, standings } from "../../data/arsenalData";

export const Competition = () => {
  const standingsTop3 = standings.filter((row) => row.pos <= 3);

  return (
    <section
      id="competicion"
      data-testid="competition-section"
      className="relative bg-[#061930] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(#9C824A 1px, transparent 1px), linear-gradient(90deg, #9C824A 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>
      <div className="absolute top-0 right-0 w-1/3 h-1 bg-[#9C824A]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-14"
        >
          <div className="section-overline">Módulo de Competición</div>
          <h2 className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight">
            Campo de <span className="text-[#DB0007]">batalla</span>
          </h2>
          <p className="font-outfit text-white/60 mt-4">
            Calendario, torneos y tabla de posiciones (primeros 3 lugares). Todo lo que necesitas para seguir a tu equipo.
          </p>
        </motion.div>

        <Tabs defaultValue="calendario" className="w-full" data-testid="competition-tabs">
          <TabsList className="bg-white/5 border border-white/10 rounded-none p-1 h-auto flex-wrap">
            <TabsTrigger
              value="calendario"
              data-testid="tab-calendario"
              className="data-[state=active]:bg-[#DB0007] data-[state=active]:text-white rounded-none font-anton uppercase tracking-widest px-6 py-3 text-white/70"
            >
              <Calendar size={16} className="mr-2" /> Calendario
            </TabsTrigger>
            <TabsTrigger
              value="torneos"
              data-testid="tab-torneos"
              className="data-[state=active]:bg-[#DB0007] data-[state=active]:text-white rounded-none font-anton uppercase tracking-widest px-6 py-3 text-white/70"
            >
              <Trophy size={16} className="mr-2" /> Torneos
            </TabsTrigger>
            <TabsTrigger
              value="tabla"
              data-testid="tab-tabla"
              className="data-[state=active]:bg-[#DB0007] data-[state=active]:text-white rounded-none font-anton uppercase tracking-widest px-6 py-3 text-white/70"
            >
              <BarChart3 size={16} className="mr-2" /> Tabla General
            </TabsTrigger>
          </TabsList>

          {/* CALENDARIO */}
          <TabsContent value="calendario" className="mt-8">
            <div className="space-y-2">
              {schedule.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`grid grid-cols-12 gap-4 items-center p-4 border-l-4 ${
                    m.status === "upcoming"
                      ? "border-[#DB0007] bg-[#0a2240]"
                      : m.status === "cancelled"
                      ? "border-white/20 bg-[#0a2240]/30 opacity-60"
                      : "border-[#9C824A] bg-[#0a2240]/50"
                  }`}
                  data-testid={`schedule-row-${m.id}`}
                >
                  <div className="col-span-12 sm:col-span-2">
                    <div className="font-anton text-2xl leading-none">
                      {new Date(m.date).getDate()}
                    </div>
                    <div className="font-mono-jb text-[10px] uppercase tracking-widest text-[#9C824A] mt-1">
                      {new Date(m.date).toLocaleDateString("es-MX", { month: "short" })}
                      <span className="ml-2 text-white/50">· J{m.jornada}</span>
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-7">
                    <div className={`font-anton uppercase text-lg ${m.status === "cancelled" ? "line-through text-white/40" : ""}`}>
                      {m.home}{" "}
                      {m.status === "played" ? (
                        <span className="text-[#DB0007] mx-1">{m.score}</span>
                      ) : m.status === "cancelled" ? (
                        <span className="text-white/30 mx-1">—</span>
                      ) : (
                        <span className="text-white/30 mx-1">VS</span>
                      )}{" "}
                      {m.away}
                    </div>
                    <div className="font-outfit text-xs text-white/50 flex items-center gap-1 mt-1">
                      <MapPin size={12} /> {m.venue}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-3 text-left sm:text-right">
                    <span className={`inline-block font-anton uppercase text-xs tracking-widest px-3 py-1 ${
                      m.status === "upcoming"
                        ? "bg-[#DB0007] text-white"
                        : m.status === "cancelled"
                        ? "bg-white/10 text-white/50 line-through"
                        : "bg-white/10 text-[#9C824A]"
                    }`}>
                      {m.status === "upcoming" ? "Próximo" : m.status === "cancelled" ? "Cancelado" : "Finalizado"}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* TORNEOS */}
          <TabsContent value="torneos" className="mt-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="group relative bg-[#0a2240] border border-white/10 p-6 hover:border-[#DB0007] transition-all cursor-pointer"
                  data-testid={`tournament-card-${t.id}`}
                >
                  <div className="absolute top-0 right-0 w-12 h-12 bg-[#DB0007] flex items-center justify-center group-hover:bg-[#9C824A] transition-colors">
                    <Trophy size={20} className="text-white" />
                  </div>
                  <div className="font-mono-jb text-xs text-[#9C824A] uppercase tracking-widest">
                    {t.season}
                  </div>
                  <h3 className="font-anton uppercase text-2xl mt-3 pr-14 leading-tight">
                    {t.name}
                  </h3>
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <span className="font-outfit text-sm text-white/60">
                      {t.status}
                    </span>
                    {t.position === 1 && (
                      <span className="font-anton text-[#9C824A] uppercase text-sm tracking-widest">
                        #1 ↑
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* TABLA */}
          <TabsContent value="tabla" className="mt-8">
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full font-outfit" data-testid="standings-table">
                <thead>
                  <tr className="bg-[#0a2240] border-b border-white/10">
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-left">#</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-left">Equipo</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center">PJ</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center">G</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center">E</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center">P</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center hidden sm:table-cell">GF</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center hidden sm:table-cell">GC</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center hidden md:table-cell">Dif</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center hidden lg:table-cell">%</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#9C824A] p-3 text-center hidden lg:table-cell">PE</th>
                    <th className="font-anton uppercase text-xs tracking-widest text-[#DB0007] p-3 text-center">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standingsTop3.map((row) => {
                    const isArsenal = row.team === "ARSENAL";
                    return (
                      <tr
                        key={row.pos}
                        className={`border-b border-white/5 transition-colors ${
                          isArsenal ? "bg-[#DB0007]/10 hover:bg-[#DB0007]/20" : "hover:bg-white/5"
                        }`}
                        data-testid={`standings-row-${row.pos}`}
                      >
                        <td className="p-3 font-anton text-lg">
                          {row.pos}
                          {row.pos <= 3 && <span className="text-[#9C824A] ml-1">●</span>}
                        </td>
                        <td className={`p-3 font-outfit ${isArsenal ? "font-bold text-white" : "text-white/80"}`}>
                          {row.team}
                          {isArsenal && <span className="ml-2 text-[10px] bg-[#DB0007] px-1.5 py-0.5 text-white uppercase tracking-widest">Nuestro Equipo</span>}
                        </td>
                        <td className="p-3 text-center text-white/70">{row.pj}</td>
                        <td className="p-3 text-center text-white/70">{row.g}</td>
                        <td className="p-3 text-center text-white/70">{row.e}</td>
                        <td className="p-3 text-center text-white/70">{row.p}</td>
                        <td className="p-3 text-center text-white/70 hidden sm:table-cell">{row.gf}</td>
                        <td className="p-3 text-center text-white/70 hidden sm:table-cell">{row.gc}</td>
                        <td className="p-3 text-center hidden md:table-cell">
                          <span className={row.dif > 0 ? "text-green-400" : row.dif < 0 ? "text-red-400" : "text-white/70"}>
                            {row.dif > 0 ? "+" : ""}{row.dif}
                          </span>
                        </td>
                        <td className="p-3 text-center text-white/70 hidden lg:table-cell font-mono-jb text-sm">{row.pct}%</td>
                        <td className="p-3 text-center text-white/70 hidden lg:table-cell font-mono-jb text-sm">{row.pe}</td>
                        <td className="p-3 text-center font-anton text-xl text-white">{row.pts}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs text-white/50 font-outfit flex-wrap">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#9C824A] rounded-full" /> Primeras 3 posiciones
              </span>
              <span className="flex items-center gap-2">
                <Clock size={12} /> Actualizado post Jornada 4
              </span>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
